import { HStack, Spacer, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuBookOpen, LuPencil, LuPlus } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  deleteContentLibraryItem,
  getBusinessFeatures,
  getContentLibrary,
  getCustomerProfileSubsets,
  getMessagingPillars,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import { ContentLibraryDialog } from "../../../components/content-library-dialog";
import DataTable, { DataTableColumn } from "../../../components/data-table";
import { DeleteButton } from "../../../components/delete-button";
import { EmptyState } from "../../../components/empty-state";
import IconCircle from "../../../components/icon-circle";
import PageBody from "../../../components/page-body";
import { useLoading, useToaster } from "../../../hooks";
import {
  BusinessFeature,
  ContentLibraryItem,
  CustomerProfileSubset,
  MessagingPillar,
} from "../../../types/company-knowledge";

export default function ContentLibraryPage() {
  const { workspace: workspaceSlug } = useParams();
  const [items, setItems] = React.useState<ContentLibraryItem[]>([]);
  const [subsets, setSubsets] = React.useState<CustomerProfileSubset[]>([]);
  const [features, setFeatures] = React.useState<BusinessFeature[]>([]);
  const [pillars, setPillars] = React.useState<MessagingPillar[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingItem, setEditingItem] =
    React.useState<ContentLibraryItem | null>(null);
  const withToaster = useToaster();

  const loadItems = React.useCallback(async () => {
    if (!workspaceSlug) return;
    await withLoading(async () => {
      await withToaster(async () => {
        // The subsets, features, and pillars ride along on every load so
        // the dialog's checkbox groups stay in sync.
        const [items, subsets, features, pillars] = await Promise.all([
          getContentLibrary(workspaceSlug),
          getCustomerProfileSubsets(workspaceSlug),
          getBusinessFeatures(workspaceSlug),
          getMessagingPillars(workspaceSlug),
        ]);
        setItems(items);
        setSubsets(subsets);
        setFeatures(features);
        setPillars(pillars);
        setHasLoadedOnce(true);
      });
    });
  }, [withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = React.useCallback(
    async (id: number) => {
      if (!workspaceSlug) return;
      await withLoading(async () => {
        await withToaster(async () => {
          await deleteContentLibraryItem(workspaceSlug, id);
          await loadItems();
        });
      });
    },
    [loadItems, withLoading, withToaster, workspaceSlug],
  );

  const columns: DataTableColumn<ContentLibraryItem>[] = [
    {
      id: "title",
      name: "Title",
      icon: <LuBookOpen />,
      width: 240,
      cell: ({ value }) => <Text fontWeight="medium">{value.title}</Text>,
    },
    {
      id: "context",
      name: "Takeaways",
      width: 320,
      cell: ({ value }) => (
        <Text color="fg.muted" truncate>
          {value.context || "—"}
        </Text>
      ),
    },
    {
      name: "Applicable ICP subsets",
      width: 200,
      cell: ({ value }) => {
        const names = subsets
          .filter((s) => value["subset-ids"].includes(s.id))
          .map((s) => s.name);
        return (
          <Text color="fg.muted" truncate>
            {names.length > 0 ? names.join(", ") : "All subsets"}
          </Text>
        );
      },
    },
    {
      name: "",
      width: 100,
      cell: ({ value }) => (
        <HStack justify="flex-end">
          <Button
            size="sm"
            title="Edit content item"
            variant="ghost"
            onClick={() => {
              setEditingItem(value);
              setIsDialogOpen(true);
            }}
          >
            <LuPencil />
          </Button>
          <DeleteButton
            label="Delete"
            message={`Delete "${value.title}"?`}
            onDelete={() => handleDelete(value.id)}
          />
        </HStack>
      ),
    },
  ];

  const isEmpty = items.length === 0 && hasLoadedOnce;

  return (
    <PageBody>
      <Stack flex="1" gap="5">
        <HStack>
          <Stack gap="0.5">
            <Text color="fg.ink" fontSize="md" fontWeight="medium">
              Content Library
            </Text>
            <Text color="fg.muted" fontSize="sm">
              Material the product should draw on, with the takeaways worth
              keeping.
            </Text>
          </Stack>
          {!isEmpty && (
            <>
              <Spacer />
              <Button
                onClick={() => {
                  setEditingItem(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Content Item
              </Button>
            </>
          )}
        </HStack>
        {isEmpty ? (
          <HStack flex="1" alignItems="center">
            <EmptyState
              icon={<IconCircle Icon={LuBookOpen} size="md" variant="flat" />}
              title="No Content Items"
              description="Add content the product should know about."
            >
              <Button
                onClick={() => {
                  setEditingItem(null);
                  setIsDialogOpen(true);
                }}
              >
                <LuPlus /> Create Content Item
              </Button>
            </EmptyState>
          </HStack>
        ) : (
          <DataTable
            data={items}
            columns={columns}
            skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
          />
        )}
      </Stack>
      <ContentLibraryDialog
        features={features}
        isOpen={isDialogOpen}
        item={editingItem}
        onOpenChange={({ open }) => {
          if (!open) setEditingItem(null);
          setIsDialogOpen(open);
        }}
        onSuccess={loadItems}
        pillars={pillars}
        subsets={subsets}
        workspaceSlug={workspaceSlug ?? ""}
      />
    </PageBody>
  );
}
